import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  useCreateDriverLead,
  useGetDriverLead,
  useGetDriverLeadDetails,
} from "../../hooks/use-driver";
import { uploadCibilDocuments } from "./utils/uploadCibilDocuments";

import StepFooterActions from "./StepFooterActions";
import CibilCheckStep from "./steps/CibilCheckStep";
import EmiCalculationStep from "./steps/EmiCalculationStep";
import DriverInformationStep from "./steps/DriverInformationStep";
import { useAuth } from "../../contexts/AuthContext";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { useEmiAdminConfig } from "../../hooks/useEmiAdminConfig";

import { Check as CheckIcon } from "@mui/icons-material";
import { StepConnector, stepConnectorClasses } from "@mui/material";
import { styled } from "@mui/material/styles";

const ModernConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 16,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: "#E6ECEA",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: "#0E7A6E",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: "#0E7A6E",
  },
}));

const StepIconRoot = styled("div")(({ ownerState }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
  transition: "all 0.2s ease",
  ...(ownerState.active && {
    backgroundColor: "#0E7A6E",
    color: "#fff",
    boxShadow: "0 0 0 4px rgba(14,122,110,0.15)",
  }),
  ...(ownerState.completed && {
    backgroundColor: "#0E7A6E",
    color: "#fff",
  }),
  ...(!ownerState.active &&
    !ownerState.completed && {
      backgroundColor: "#F3F5F4",
      color: "#98A6A0",
      border: "1px solid #E6ECEA",
    }),
}));

const ModernStepIcon = (props) => {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : icon}
    </StepIconRoot>
  );
};

const validateDriverInformation = (data) => {
  const requiredFields = [
    "driverName",
    "phoneNumber",
    "vehicleFinanced",
    "age",
    "city",
    "address",
  ];

  return requiredFields.every(
    (field) => String(data[field] || "").trim() !== "",
  );
};

const validateCibilCheck = (data) => {
  return (
    Boolean(data.aadhaarFrontUrl) &&
    Boolean(data.aadhaarBackUrl) &&
    Boolean(data.panCardUrl) &&
    String(data.cibilNumber || "").trim() !== ""
  );
};

const validateEmiCalculation = () => true;

const STEPS = [
  {
    key: "driverInformation",
    label: "Driver Information",
    Component: DriverInformationStep,
    validate: validateDriverInformation,
    initialData: {
      driverName: "",
      phoneNumber: "",
      alternatePhoneNumber: "",
      age: "",
      eVehicleNumber: "",
      city: "",
      location: "",
      address: "",
      vehicleFinanced: "",
      //   financierName: "",
      //   financierPhoneNumber: "",
      //   financierAddress: "",

      vehicleFinancierDetails: {
        name: "",
        phoneNumber: "",
        address: "",
      },
    },
  },
  {
    key: "cibilCheck",
    label: "CIBIL Check",
    Component: CibilCheckStep,
    validate: validateCibilCheck,
    initialData: {
      aadhaarFrontUrl: null,
      aadhaarBackUrl: null,
      panCardUrl: null,
      cibilNumber: "",
    },
  },
  {
    key: "emiCalculation",
    label: "EMI Calculation",
    Component: EmiCalculationStep,
    validate: validateEmiCalculation,
    initialData: {
      invoiceAmount: "",
      tenure: "",
      downPayment: "",
    },
  },
];

const buildInitialFormData = () =>
  STEPS.reduce((acc, step) => {
    acc[step.key] = structuredClone(step.initialData);
    return acc;
  }, {});

const getLeadIdFromUrl = (searchParams) => {
  return searchParams.get("leadId");
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return "";

  if (value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }

  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = normalizeValue(value[key]);
        return result;
      }, {});
  }

  return value;
};

const isEqual = (first, second) => {
  return (
    JSON.stringify(normalizeValue(first)) ===
    JSON.stringify(normalizeValue(second))
  );
};

const getInitialStepFromLead = (lead) => {
  if (!lead) return 0;

  if (
    lead.cibilCheck?.aadhaarFrontUrl &&
    lead.cibilCheck?.aadhaarBackUrl &&
    lead.cibilCheck?.panCardUrl &&
    lead.cibilCheck?.cibilNumber
  ) {
    return 2;
  }

  if (lead.driverInformation) {
    return 1;
  }

  return 0;
};

const mapLeadToFormData = (lead) => ({
  driverInformation: {
    driverName: lead?.driverName || "",
    phoneNumber: lead?.phoneNumber || "",
    alternatePhoneNumber: lead?.alternatePhoneNumber || "",
    age: lead?.age || "",
    eVehicleNumber: lead?.eVehicleNumber || "",
    city: lead?.city || "",
    location: lead?.location || "",
    address: lead?.address || "",
    vehicleFinanced: lead?.vehicleFinanced || "",

    // financierName: lead?.vehicleFinancier?.name || "",
    // financierPhoneNumber: lead?.vehicleFinancier?.phoneNumber || "",
    // financierAddress: lead?.vehicleFinancier?.address || "",

    vehicleFinancier: {
      name: lead?.vehicleFinancier?.name || "",
      phoneNumber: lead?.vehicleFinancier?.phoneNumber || "",
      address: lead?.vehicleFinancier?.address || "",
    },
  },

  cibilCheck: {
    aadhaarFrontUrl: lead?.cibilCheck?.aadhaarFrontUrl || null,
    aadhaarBackUrl: lead?.cibilCheck?.aadhaarBackUrl || null,
    panCardUrl: lead?.cibilCheck?.panCardUrl || null,
    cibilNumber: lead?.cibilCheck?.cibilNumber || "",
  },

  emiCalculation: {
    invoiceAmount: lead?.loanSummary?.invoiceAmount || "",
    tenure: lead?.loanSummary?.tenure || "",
    downPayment: lead?.loanSummary?.downPayment || "",
  },
});

const getCibilDocuments = (cibilCheck) => ({
  aadhaarFrontUrl: cibilCheck?.aadhaarFrontUrl,
  aadhaarBackUrl: cibilCheck?.aadhaarBackUrl,
  panCardUrl: cibilCheck?.panCardUrl,
});

const hasNewCibilDocuments = (currentCibil, savedCibil) => {
  const currentDocuments = getCibilDocuments(currentCibil);
  const savedDocuments = getCibilDocuments(savedCibil);

  return !isEqual(currentDocuments, savedDocuments);
};

const LeadCreationStepper = () => {
  const { user } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  const urlLeadId = getLeadIdFromUrl(searchParams);

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(buildInitialFormData);
  const [savedFormData, setSavedFormData] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [leadId, setLeadId] = useState(urlLeadId);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(Boolean(urlLeadId));

  const [isSaving, setIsSaving] = useState(false);

  const {
    mutateAsync: createDriverLead,
    isPending: isCreatingLead,
    isError,
    error,
  } = useCreateDriverLead();

  const { data: emiAdminConfig } = useEmiAdminConfig("v2");

  const {
    data: leadResponse,
    isLoading: isLoadingLead,
    isError: isLeadFetchError,
    error: leadFetchError,
  } = useGetDriverLeadDetails(leadId);

  useEffect(() => {
    if (!urlLeadId) {
      if (leadId !== null) {
        setLeadId(null);
        setFormData(buildInitialFormData());
        setSavedFormData(null);
        setActiveStep(0);
        setShowErrors(false);
        setUploadError(null);
      }
      setIsInitializing(false);
      return;
    }

    if (isLoadingLead) return;

    if (leadResponse) {
      const loadedFormData = mapLeadToFormData(leadResponse);
      setLeadId(leadResponse.leadId || urlLeadId);
      setFormData(loadedFormData);
      setSavedFormData(loadedFormData);
      setActiveStep(getInitialStepFromLead(leadResponse));
    }

    setIsInitializing(false);
  }, [urlLeadId, leadResponse, isLoadingLead]);

  const currentStep = STEPS[activeStep];
  const currentStepData = formData[currentStep.key];

  const isStepValid = useMemo(
    () => currentStep.validate(currentStepData),
    [currentStep, currentStepData],
  );

  const isLastStep = activeStep === STEPS.length - 1;

  const hasStepChanged = (stepKey) => {
    if (!savedFormData) return true;

    const currentStepValue = formData[stepKey];
    const savedStepValue =
      savedFormData[stepKey] ??
      STEPS.find((s) => s.key === stepKey)?.initialData;

    return !isEqual(currentStepValue, savedStepValue);
  };

  const hasFormChanged = useMemo(
    () => hasStepChanged(currentStep.key),
    [formData, savedFormData, currentStep.key],
  );

  const updateStepData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [currentStep.key]: {
        ...prev[currentStep.key],
        [field]: value,
      },
    }));
  };

  const updateSavedFormData = (partial) => {
    setSavedFormData((prev) => ({
      ...(prev || buildInitialFormData()),
      ...partial,
    }));
  };

  const saveLead = async (payload) => {
    const response = await createDriverLead(payload);

    const returnedId =
      response?.leadId ||
      response?.id ||
      response?.data?.leadId ||
      response?.data?.id;

    if (returnedId) {
      setLeadId(returnedId);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("leadId", returnedId);
        return params;
      });
    }

    return { response, leadId: returnedId };
  };

  const saveDriverInformation = async () => {
    if (!hasFormChanged && leadId) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    const payload = {
      leadId: leadId || undefined,
      driverInformation: formData.driverInformation,
      dealer: {
        id: user?.sub || "",
        name: user?.name || "",
        email: user?.email || "",
        city: user?.city || "",
      },
      createdBy: user?.email || "",
    };

    await saveLead(payload);
    updateSavedFormData({ driverInformation: formData.driverInformation });
    setActiveStep((prev) => prev + 1);
  };

  const saveCibilInformation = async () => {
    const savedCibil = savedFormData?.cibilCheck;
    const cibilChanged = !isEqual(
      formData.cibilCheck,
      savedCibil || STEPS[1].initialData,
    );

    if (!cibilChanged && leadId) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    let cibilPayload = formData.cibilCheck;

    if (hasNewCibilDocuments(formData.cibilCheck, savedCibil)) {
      setIsUploading(true);
      try {
        cibilPayload = await uploadCibilDocuments(formData.cibilCheck, leadId);
      } catch (err) {
        setUploadError(getErrorMessage(err, "Failed to upload documents"));
        throw err;
      } finally {
        setIsUploading(false);
      }
    }

    await saveLead({
      leadId,
      driverInformation: formData.driverInformation,
      cibilCheck: cibilPayload,
    });

    setFormData((prev) => ({
      ...prev,
      cibilCheck: { ...prev.cibilCheck, ...cibilPayload },
    }));
    updateSavedFormData({ cibilCheck: cibilPayload });
    setActiveStep((prev) => prev + 1);
  };

  const handleNext = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setShowErrors(true);

    if (!isStepValid) {
      setIsSaving(false);
      return;
    }

    setShowErrors(false);
    setUploadError(null);

    try {
      if (currentStep.key === "driverInformation") {
        await saveDriverInformation();
        return;
      }
      if (currentStep.key === "cibilCheck") {
        await saveCibilInformation();
        return;
      }
      setActiveStep((prev) => prev + 1);
    } catch (err) {
      setUploadError(
        getErrorMessage(err, "Something went wrong while saving the lead."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    setShowErrors(false);
    setUploadError(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const StepComponent = currentStep.Component;
  const isBusy = isUploading || isCreatingLead;

  const isActionBtnDisabled =
    emiAdminConfig?.minCIBIL &&
    currentStepData.cibilNumber &&
    Number(currentStepData.cibilNumber) < Number(emiAdminConfig.minCIBIL);

  const loadingLabel = isUploading ? "Uploading..." : "Saving...";

  if (isInitializing || isLoadingLead) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          border: "1px solid #E6ECEA",
          boxShadow: "0 1px 2px rgba(16,24,22,0.04)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 6,
          }}
        >
          <CircularProgress size={28} sx={{ color: "#0E7A6E" }} />
          <Typography sx={{ fontSize: 13, color: "#66766F", fontWeight: 500 }}>
            Loading lead details…
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (isLeadFetchError) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          border: "1px solid #E6ECEA",
          boxShadow: "0 1px 2px rgba(16,24,22,0.04)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#B3261E" }}>
          {leadFetchError?.message || "Failed to load lead."}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: "1px solid #E6ECEA",
        boxShadow: "0 1px 2px rgba(16,24,22,0.04)",
        p: { xs: 2, md: 4 },
      }}
    >
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<ModernConnector />}
      >
        {STEPS.map((step) => (
          <Step key={step.key} sx={{ flex: 1, minWidth: 0, px: { xs: 0.5, sm: 1 } }}>
            <StepLabel
              StepIconComponent={ModernStepIcon}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#66766F",
                  mt: 1,
                  "&.Mui-active": { color: "#12181A", fontWeight: 700 },
                  "&.Mui-completed": { color: "#12181A" },
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 5, mt: 8 }}>
        <StepComponent
          data={currentStepData}
          onChange={updateStepData}
          showErrors={showErrors}
          allFormData={formData}
          leadId={leadId}
        />
      </Box>

      {(isError || uploadError) && (
        <Typography sx={{ fontSize: 13, color: "#B3261E", mb: 2 }}>
          {uploadError ||
            error?.message ||
            "Something went wrong while saving the lead."}
        </Typography>
      )}

      {currentStep.key === "emiCalculation" ? (
        <Box sx={{ pt: 3, borderTop: "1px solid #E6ECEA" }}>
          <Button
            onClick={handleBack}
            sx={{
              borderRadius: 2,
              color: "#3A4441",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { backgroundColor: "#F1F3F2" },
            }}
          >
            Back
          </Button>
        </Box>
      ) : (
        <StepFooterActions
          activeStep={activeStep}
          onBack={handleBack}
          onNext={handleNext}
          isLastStep={isLastStep}
          loading={isBusy}
          loadingLabel={loadingLabel}
          disabled={isActionBtnDisabled}
        />
      )}
    </Paper>
  );
};

export default LeadCreationStepper;